/**
 * Environment Configuration
 * Centralized configuration management for different environments
 */

// Environment detection
export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';
export const isTest = process.env.NODE_ENV === 'test';
export const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';

// Application Configuration
export const appConfig = {
  name: process.env.REACT_APP_NAME || 'Poultry Management System',
  version: process.env.REACT_APP_VERSION || '1.0.0',
  environment:
    process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development',
};

// API Configuration
export const apiConfig = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS) || 3,
};

// Authentication Configuration
export const authConfig = {
  jwtSecretKey: process.env.REACT_APP_JWT_SECRET_KEY || 'default-secret-key',
  jwtExpiry: process.env.REACT_APP_JWT_EXPIRY || '24h',
  refreshTokenExpiry: process.env.REACT_APP_REFRESH_TOKEN_EXPIRY || '7d',
};

// Feature Flags
export const featureFlags = {
  enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
  enableDebugMode: process.env.REACT_APP_ENABLE_DEBUG_MODE === 'true',
  enableMockData: process.env.REACT_APP_ENABLE_MOCK_DATA === 'true',
  enableOfflineMode: process.env.REACT_APP_ENABLE_OFFLINE_MODE === 'true',
};

// External Services Configuration
export const externalServices = {
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  sentryDsn: process.env.REACT_APP_SENTRY_DSN || '',
  analyticsId: process.env.REACT_APP_ANALYTICS_ID || '',
};

// File Upload Configuration
export const fileUploadConfig = {
  maxFileSize: parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 10485760, // 10MB
  allowedFileTypes: (
    process.env.REACT_APP_ALLOWED_FILE_TYPES || 'jpg,jpeg,png,pdf,csv,xlsx'
  ).split(','),
  uploadEndpoint: process.env.REACT_APP_UPLOAD_ENDPOINT || '/api/upload',
};

// UI Configuration
export const uiConfig = {
  defaultPageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGE_SIZE) || 10,
  maxPageSize: parseInt(process.env.REACT_APP_MAX_PAGE_SIZE) || 100,
  toastDuration: parseInt(process.env.REACT_APP_TOAST_DURATION) || 5000,
};

// Security Configuration
export const securityConfig = {
  enableCSP: process.env.REACT_APP_ENABLE_CSP === 'true',
  allowedOrigins: (
    process.env.REACT_APP_ALLOWED_ORIGINS || 'http://localhost:3000'
  ).split(','),
};

// Development Configuration
export const devConfig = {
  mockApiDelay: parseInt(process.env.REACT_APP_MOCK_API_DELAY) || 1000,
  enableReduxDevtools: process.env.REACT_APP_ENABLE_REDUX_DEVTOOLS === 'true',
  logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
};

// Validation function to check required environment variables
export const validateEnvironment = () => {
  const errors = [];

  // Check required variables based on environment
  if (isProduction) {
    if (!process.env.REACT_APP_API_BASE_URL) {
      errors.push('REACT_APP_API_BASE_URL is required in production');
    }

    if (
      !process.env.REACT_APP_JWT_SECRET_KEY ||
      process.env.REACT_APP_JWT_SECRET_KEY.includes('default')
    ) {
      errors.push(
        'REACT_APP_JWT_SECRET_KEY must be set to a secure value in production'
      );
    }
  }

  // Validate API URL format
  if (apiConfig.baseUrl && !isValidUrl(apiConfig.baseUrl)) {
    errors.push('REACT_APP_API_BASE_URL must be a valid URL');
  }

  // Validate numeric values
  if (isNaN(apiConfig.timeout) || apiConfig.timeout <= 0) {
    errors.push('REACT_APP_API_TIMEOUT must be a positive number');
  }

  if (
    isNaN(fileUploadConfig.maxFileSize) ||
    fileUploadConfig.maxFileSize <= 0
  ) {
    errors.push('REACT_APP_MAX_FILE_SIZE must be a positive number');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper function to validate URL
const isValidUrl = string => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

// Get configuration summary for debugging
export const getConfigSummary = () => {
  return {
    app: appConfig,
    api: {
      ...apiConfig,
      // Don't expose sensitive data in logs
      baseUrl: apiConfig.baseUrl.replace(/\/\/.*@/, '//***@'),
    },
    features: featureFlags,
    ui: uiConfig,
    environment: {
      isDevelopment,
      isProduction,
      isTest,
      isStaging,
    },
  };
};

// Export all configurations as a single object
export const config = {
  app: appConfig,
  api: apiConfig,
  auth: authConfig,
  features: featureFlags,
  externalServices,
  fileUpload: fileUploadConfig,
  ui: uiConfig,
  security: securityConfig,
  dev: devConfig,
  environment: {
    isDevelopment,
    isProduction,
    isTest,
    isStaging,
  },
};

export default config;
